import { startIdentityVerification } from '@ks/identity-verification';
import { request } from '../request';
import { generateCallback, stringify, IS_PROD } from '../utils';
import { getConfig } from './init';
import { createDevLog } from '../log';
import { PASSPORT_PREFIX } from '../config';

const devLog = createDevLog('general-sso:service');
export * from './init';

const DEFAULT_COUNTRY_CODE = '+86';

export interface BaseRes {
    result: number;
    error_msg?: string;
}

/**
 * 授权认证公共接口
 */
export type AuthTokenResponse = {
    result: number;
    error_msg?: string;
    sid: string;
    ssecurity: string;
    userId: number;
    stsUrl: string;
    followUrl: string;
    isNewUser: boolean;
    passToken: string;
    userInfos?: any[];
} & {
    [key: string]: string;
};
export interface AuthTokenResult {
    result: number;
    error_msg?: string;
    authToken?: string;
    sid?: string;
    callback?: string;
    serviceToken?: string;
    ssecurity?: string;
    userId?: number;
    stsUrl?: string;
    followUrl?: string;
    isNewUser?: boolean;
    multiUserToken?: string;
    userInfos?: any[];
}
function getLoginToken(url: string, params: any): Promise<AuthTokenResult> {
    const config = getConfig();
    const currentSid = params.sid ?? config.sid;
    return request<AuthTokenResponse>(url, params).then(({
        [currentSid + '.at']: authToken,
        sid = currentSid,
        [currentSid + '_st']: serviceToken,
        userId,
        ssecurity,
        stsUrl,
        followUrl,
        isNewUser,
        [currentSid + '.mt']: multiUserToken,
        userInfos = [],
        result,
        error_msg,
    }) => {
        return {
            [currentSid + '.at']: authToken,
            authToken,
            sid,
            ssecurity,
            callback: generateCallback(config.callback, {authToken, sid}),
            [currentSid + '_st']: serviceToken,
            serviceToken,
            userId,
            stsUrl,
            followUrl,
            isNewUser,
            [currentSid + '.mt']: multiUserToken,
            multiUserToken,
            userInfos,
            result,
            error_msg,
        };
    }).catch(res => {
        const { result } = res;
        // 返回在400001-410999之间时，视为需要快手验证码，会使用统一身份认证SDK打开验证页面
        if (result >= 400001 && result <= 410999) {
            return startIdentityVerification({
                url: res.url,
                language: getConfig().language as Parameters<typeof startIdentityVerification>[0]['language'],
                qrcodeEnv: getConfig().env
            })
            // 滑块验证通过
            .then(res => {
                if(res.result === -999) {
                    // 行为验证码和端外短信验证出现取消，直接向下透传
                    return Promise.reject(res);
                }
                return getLoginToken(url, {
                    ...params,
                    ztIdentityVerificationType: res.type,
                    ztIdentityVerificationCheckToken: res.token,
                });
            })
            // 滑块验证失败
            .catch(res => {
                // result=2表示captchaSession 过期，需要重新获取 captchaSession
                if (res.result === 2) {
                    // 重新获取captchaSession
                    return getLoginToken(url, params);
                }
                // 其他错误result视为非常规错误，直接抛出由业务方处理
                return Promise.reject(res);
            });
        }
        return Promise.reject(res);
    });
}

/**
 * 身份认证统一拦截器
 */
function identityVerificationInterceptor(url: string, params: any): Promise<BaseRes> {
    return request<BaseRes>(url, params).catch((res) => {
        // 返回在400001-410999之间时，视为需要快手验证码，会使用统一身份认证SDK打开验证页面
        if (res.result >= 400001 && res.result <= 410999) {
            return startIdentityVerification({
                url: res.url!,
                language: getConfig().language as Parameters<typeof startIdentityVerification>[0]['language'],
                qrcodeEnv: getConfig().env
            })
            // 滑块验证通过
            .then(res => {
                if(res.result === -999) {
                    // 行为验证码和端外短信验证出现取消，直接向下透传
                    return Promise.reject(res);
                }
                return identityVerificationInterceptor(url, {
                    ...params,
                    ztIdentityVerificationType: res.type,
                    ztIdentityVerificationCheckToken: res.token,
                });
            })
            // 滑块验证失败
            .catch(res => {
                // result=2表示captchaSession 过期，需要重新获取 captchaSession
                if (res.result === 2) {
                    // 重新获取captchaSession
                    return identityVerificationInterceptor(url, params);
                }
                // 其他错误result视为非常规错误，直接抛出由业务方处理
                return Promise.reject(res);
            });
        }
        return Promise.reject(res);
    });
}

/**
 * 请求短信验证码
 */
export interface requestSmsCodeData {
    type: number;
    phone: string;
    countryCode?: string;
    ztIdentityVerificationType?: number;
    ztIdentityVerificationCheckToken?: string;
}
export interface requestSmsCodeResponse {
    result: number;
    error_msg?: string;
    url?: string;
    sid?: string;
}
export function requestSmsCode({
    type,
    phone,
    countryCode = DEFAULT_COUNTRY_CODE,
}: requestSmsCodeData): Promise<requestSmsCodeResponse> {
    const config = getConfig();
    return identityVerificationInterceptor(`/pass/${config.appName}/web/sms/code`, {
        type,
        phone,
        countryCode,
    });
}
export const requestMobileCode = requestSmsCode; // 别名

/**
 * 短信验证码登录
 */
export interface smsCodeLoginData {
    phone: string;
    countryCode?: string;
    smsCode: string | number;
    createId?: boolean;
}
export function smsCodeLogin({
    phone,
    countryCode = DEFAULT_COUNTRY_CODE,
    smsCode,
    createId = true,
}: smsCodeLoginData) {
    const config = getConfig();
    return getLoginToken(`/pass/${config.appName}/web/login/mobileCode`, {
        sid: config.sid,
        countryCode,
        phone,
        smsCode,
        registerOpen: createId,
    });
}
export const login = smsCodeLogin; // 别名

/**
 * 密码登录
 */
export interface PhonePasswordLogin {
    password: string;
    phone: string;
    countryCode?: string;
}
export function passwordLogin({
    phone,
    countryCode = DEFAULT_COUNTRY_CODE,
    password,
}: PhonePasswordLogin) {
    const config = getConfig();
    const params = {
        sid: config.sid,
        password,
        countryCode,
        phone,
    };
    return getLoginToken(`/pass/${config.appName}/web/login/mobilePassword`, {
        ...params,
    });
}

/**
 * 多帐号下选择用户
 */
export interface MultiUserInfoForPhone {
    userId: number;
    multiUserToken: string;
    phone: string;
    countryCode?: string;
}
export function chooseUser({ userId, multiUserToken, phone, countryCode }: MultiUserInfoForPhone): Promise<AuthTokenResult>;
export function chooseUser({
    userId,
    multiUserToken,
    phone,
    countryCode = DEFAULT_COUNTRY_CODE,
}: any) {
    const config = getConfig();
    return getLoginToken(`/pass/${config.appName}/web/login/multiTokenLogin`, {
        sid: config.sid,
        countryCode,
        phone,
        userId,
        multiUserToken,
    });
}

/**
 * @deprecated
 * 同步token
 */
export function passToken(sid?: string, options?: {
    baseUrl?: string;
}) {
    if (process?.env?.NODE_ENV !== 'production') {
        console.warn('Warning: `passToken` will be deprecated');
    }
    const config = getConfig();
    return getLoginToken(`/pass/${config.appName}/web/login/passToken`, {
        sid: sid ?? config.sid,
        baseUrl: options?.baseUrl,
    });
}

export interface RefreshLoginParam {
    sid: string,
    passTokenUrl: string,
    stsUrl: string,
    followUrl?: string,
    openFollowUrlInNewWindow?: boolean,
    pullTokenFromCenter?: boolean,
    // 超时时间 单位毫秒
    timeout?: number,
    setRootDomain?: boolean; // 根域下设置登录态
}

export interface RefreshLoginResult {
    result: number,
    message?: string
}

export interface MessageEventWithRefreshResult extends MessageEvent {
    data: string,
}

const REFRESH_LOGIN_STATUS_TIMEOUT = 3000 // 3s

/**
 * 同步登录状态
 *
 * @param {String} param.sid 业务sid
 * @param {String} param.passTokenUrl 当前业务域名可以携带passToken Cookie 的地址
 * @param {String} param.stsUrl 更新种植Cookie 地址
 * @param {String?} param.followUrl [可选] 成功后去往的地址
 * @param {Boolean?} param.pullTokenFromCenter [可选] 使用中心账户，需要 init 配置 bizIdHost。默认值false
 *
 * successUrl [内部使用]通知本函数调用成功
 * failureUrl [内部使用]通知本函数调用失败
 * pullTokenURL 从中心帐号拉取token网址
 *
 * iframe 访问网址依次为
 *     (1) passTokenURL
 *     ┣ 错误 →    ┳ pullTokenFromCenter === true → (4)pullTokenURL  ┳ 错误 failureUrl
 *     | 成功      ↓ pullTokenFromCenter ==== false                  ↓ 成功
 *     ↓            failureUrl                                    successUrl
 *     (2) stsURL
 *     ┣ 错误 → failureUrl
 *     ↓ 成功
 *     (3) successUrl
 *
 * 成功后如果有followURL值，则 location.href 跳转 followURL
 *
 * @see 文档地址： https://docs.corp.kuaishou.com/d/home/fcABKLV-jG-eT61dH9fIHIsh5#
 *
 * @return {Promise} promise 调用成功{result: 1}, 失败{result: 0, message?: 'reason' }
 */
export async function refreshLoginStatus(param: RefreshLoginParam): Promise<RefreshLoginResult> {
    const config = getConfig();
    const {
        passTokenUrl,
        stsUrl,
        sid: paramSid,
        pullTokenFromCenter = false,
        followUrl,
        openFollowUrlInNewWindow = false,
    } = param;
    const sid = paramSid ?? config.sid;
    const MSG_ID = 'G_SSO_' + Date.now();
    devLog("params",{ passTokenUrl, stsUrl, sid });
    const baseResultUrl = config.env === 'production'
        ? `${PASSPORT_PREFIX.production}/pc/account/passToken/result`
        : `${PASSPORT_PREFIX.development}/pc/account/passToken/result`;
    const successUrl = `${baseResultUrl}?${stringify({
        successful: true,
        id: MSG_ID,
    })}`;
    const failureUrl = `${baseResultUrl}?${stringify({
        successful: false,
        id: MSG_ID,
    })}`;
    let passTokenFailureUrl: string;
    if (pullTokenFromCenter) {
        const baseUrl = config?.baseUrl ?? '';
        if(baseUrl === '') {
            console.warn('pullTokenFromCenter mode need config baseUrl in init');
            console.warn('');
        }
        devLog("pullTokenUrl", {
            base: `${config.baseUrl}/pass/kuaishou/login/pull`,
            followUrl: successUrl + '&for=pullToken',
            loginUrl: failureUrl + '&for=pullToken',
            sid,
        });
        passTokenFailureUrl = `${config.baseUrl}/pass/kuaishou/login/pull?${stringify({
            sid,
            followUrl: successUrl + '&for=pullToken',
            loginUrl: failureUrl + '&for=pullToken',
        })}`;

    } else {
        passTokenFailureUrl = failureUrl + '&for=pullTokenFail';
    }
    devLog("passTokenFailureUrl",{
        pullTokenFromCenter,
        value: passTokenFailureUrl
    });
    devLog("passTokenSuccessUrl",{
        base: stsUrl,
        followUrl: successUrl + '&for=passTokenSuccess',
        loginUrl: failureUrl + '&for=passTokenSuccess',
    });
    const passTokenSuccessUrl = `${stsUrl}?${stringify({
        followUrl: successUrl + '&for=passTokenSuccess',
        failUrl: failureUrl + '&for=passTokenSuccess',
        setRootDomain: !!param.setRootDomain,
    })}`;
    devLog("finalPassTokenUrl",{
        base: passTokenUrl,
        callback: passTokenSuccessUrl,
        __loginPage: passTokenFailureUrl,
        sid,
    });
    const finalPassTokenUrl = `${passTokenUrl}?${stringify({
        callback: passTokenSuccessUrl,
        __loginPage: passTokenFailureUrl,
        sid,
    })}`;

    devLog('pass token generated final pass token url: ', finalPassTokenUrl);

    const iframe = document.createElement('iframe');
    iframe.src = finalPassTokenUrl;
    iframe.style.border = '0';
    iframe.style.width = '0';
    iframe.style.height = '0';
    document.body.appendChild(iframe);

    return new Promise((resolve) => {
        const listener = (event: MessageEventWithRefreshResult) => {
            devLog('pass token refresh result: ', event);
            const data = JSON.parse(event.data);
            if (data.id !== MSG_ID) {
                // 忽略其他的
                devLog('ignore msg', data.id);
                return;
            }
            const successful = data.successful === 'true';
            if (successful && followUrl) {
                if (openFollowUrlInNewWindow) {
                    window.open(followUrl);
                } else {
                    location.href = followUrl;
                }
            }
            const resolveResult: RefreshLoginResult = { result: successful ? 1 : 0 };
            data.failReason && (resolveResult.message = data.failReason);
            resolve(resolveResult);
            document.body.removeChild(iframe);
            window.removeEventListener('message', listener);
        };
        window.addEventListener('message', listener);
        const timeout = param.timeout && param.timeout > 0
            ? param.timeout
            : REFRESH_LOGIN_STATUS_TIMEOUT;
        setTimeout(() => resolve({
            result: 0,
            message: `timeout (max ${timeout}ms)`
        }), timeout);
    });
}

/**
 * 短信重置密码
 */
export interface resetPasswordByPhoneData {
    phone: string;
    countryCode?: string;
    password: string;
    smsCode: string | number;
    setCookie?: boolean;
}
export function resetPasswordByPhone({
    countryCode = DEFAULT_COUNTRY_CODE,
    phone,
    smsCode,
    password,
    setCookie = true,
}: resetPasswordByPhoneData) {
    const config = getConfig();
    return getLoginToken(`/pass/${config.appName}/web/password/reset`, {
        sid: config.sid,
        countryCode,
        phone,
        smsCode,
        password,
        setCookie,
    });
}

/**
 * 登出
 */
export function logout() {
    const config = getConfig();
    return request<BaseRes>(`/pass/${config.appName}/web/logout`, {
        sid: config.sid,
    });
}

/**
 * 手机号+短信验证码+密码注册
 */
export interface smsCodeRegisterData {
    phone: string;
    countryCode?: string;
    smsCode: string | number;
    password: string;
    setCookie?: boolean;
}
export function smsCodeRegister({
    phone,
    countryCode = DEFAULT_COUNTRY_CODE,
    smsCode,
    password,
    setCookie = true,
}: smsCodeRegisterData) {
    const config = getConfig();
    return getLoginToken(`/pass/${config.appName}/web/register/mobilePassword`, {
        sid: config.sid,
        countryCode,
        phone,
        smsCode,
        password,
        setCookie,
    });
}

/**
 * 邮箱+邮箱验证码+密码注册
 */
export interface emailRegisterData {
    email: string;
    emailCode: string | number;
    password: string;
    setCookie?: boolean;
}
export function emailRegister({
    email,
    emailCode,
    password,
    setCookie = true,
}: emailRegisterData) {
    const config = getConfig();
    return getLoginToken(`/pass/${config.appName}/web/register/emailPassword`, {
        sid: config.sid,
        email,
        emailCode,
        password,
        setCookie,
    });
}

/**
 * 邮箱+密码登录
 */
export interface emailPasswordLoginData {
    email: string;
    password: string;
}
export function emailPasswordLogin({
    email,
    password,
}: emailPasswordLoginData) {
    const config = getConfig();
    return getLoginToken(`/pass/${config.appName}/web/login/emailPassword`, {
        sid: config.sid,
        email,
        password,
    });
}

/**
 * 发送邮箱验证码
 */
export interface requestEmailCodeDataWithEmail {
    email: string;
    type: number;
}
export function requestEmailCode({
    email,
    type,
}: requestEmailCodeDataWithEmail): Promise<BaseRes> {
    const config = getConfig();
    return identityVerificationInterceptor(`/pass/${config.appName}/web/email/code`, {
        sid: config.sid,
        email,
        type,
    });
}

/**
 * 通过邮箱重置密码
 */
export interface resetPasswordByEmailData {
    email: string;
    password: string;
    emailCode: string | number;
    setCookie: boolean;
}
export function resetPasswordByEmail({
    email,
    password,
    emailCode,
    setCookie,
}: resetPasswordByEmailData) {
    const config = getConfig();
    return getLoginToken(`/pass/${config.appName}/web/password/resetByEmail`, {
        sid: config.sid,
        email,
        password,
        emailCode,
        setCookie,
    });
}

/**
 * 检查密码强度
 */
export interface checkPassStrengthData {
    password: string;
}
export function checkPassStrength({
    password,
}: checkPassStrengthData) {
    const config = getConfig();
    return request<BaseRes>(`/pass/${config.appName}/web/password/checkPassword`, {
        sid: config.sid,
        password,
    });
}

/**
 * 检查手机号是否可用
 */
export interface checkPhoneAvailabilityData {
    phone: string;
    countryCode?: string;
}
export function checkPhoneAvailability({
    phone,
    countryCode = DEFAULT_COUNTRY_CODE,
}: checkPhoneAvailabilityData) {
    const config = getConfig();
    return identityVerificationInterceptor(`/pass/${config.appName}/web/account/checkMobileExist`, {
        sid: config.sid,
        phone,
        countryCode,
    });
}

/**
 * 检查邮箱是否可用
 */
export interface checkEmailAvailabilityData {
    email: string;
}
export function checkEmailAvailability({
    email,
}: checkEmailAvailabilityData) {
    const config = getConfig();
    return identityVerificationInterceptor(`/pass/${config.appName}/web/account/checkEmailExist`, {
        sid: config.sid,
        email,
    });
}
